import { useState, useEffect } from "react";

import {
  Input,
  Select,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { useNavigate } from "react-router-dom";

import AddIcon from "@/assets/AddIcon";
import {
  ALLOWED_CHARACTERS_REGEX,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  DURATION_TOAST,
  INVOICE_STATUS,
  PAYMENT_STATUS,
  ROLES,
  SIRE_REVIEW_INVOICE_TYPE,
  COLORS,
} from "@/common/constants";
import ModalDelete from "@/components/Modals/ModalDelete";
// import ModalBulkPaymentConfirmation from '@/components/Modals/ModalBulkPaymentConfirmation';
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import {
  setDataTotalNewInvoiceInspectingCompany,
  setDataTotalNewInvoiceShipOperator,
} from "@/redux/slices/notificationSlice";
import { downloadFile, downloadFileZip } from "@/services/downloadService";
import {
  getNewRecordCounterAdminFinance,
  getNewRecordCounterInspectingCompany,
  getNewRecordCounterSuperAdmin,
} from "@/services/newRecordCounterService";
import TableInvoiceSireReviewInspector from "@/components/Tables/TableInvoiceSireReviewInspector";
import {
  deleteSireReviewReviewerInvoice,
  getListSireReviewInvoices,
  getListSireReviewInvoicesByReviewer,
  getListSireReviewInvoicesForFinance,
  postUpdateInvoiceStatusInspector,
  postUpdateSireReviewReviewerInvoicePayment,
} from "@/services/InvoiceSireReviewReviewerService";
import ModalSireReviewerInvoiceConfirm from "@/components/Modals/ModalSireReviewerInvoiceConfirm";
import ModalSireReviewerPaymentConfirm from "@/components/Modals/ModalSireReviewerPaymentConfirm";
import {
  ListSireReviewInvoices,
  SireReviewInvoice,
} from "@/types/sireReviewInvoice.interface";

const InvoicesSireReviewInspectorList = () => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenConfirmInvoice,
    onOpen: onOpenConfirmInvoice,
    onClose: onCloseConfirmInvoice,
  } = useDisclosure();
  // const { isOpen: isOpenBulk, onClose: onCloseBulk } = useDisclosure();
  const {
    isOpen: isOpenConfirmPayment,
    onOpen: onOpenConfirmPayment,
    onClose: onCloseConfirmPayment,
  } = useDisclosure();
  const role = useAppSelector((state: RootState) => state.account.roleName);
  // const dataRaw = useAppSelector((state: RootState) => state.payment.multiplePayment);
  const name = useAppSelector((state: RootState) => state.account.name);
  const inspectorId = useAppSelector(
    (state: RootState) => state?.account?.appId,
  );
  const toast = useToast();
  const dispatch = useAppDispatch();

  const [approvalNote, setApprovalNote] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("-1");
  const [currentPageNumber, setCurrentPageNumber] =
    useState(DEFAULT_PAGE_NUMBER);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dataList, setDataList] = useState<SireReviewInvoice[]>([]);
  const [errorMessageApproval, setErrorMessageApproval] = useState("");
  const [errorMessageNote, setErrorMessageNote] = useState("");
  const [hasNextPage, setHasNextPage] = useState(false);
  const [id, setId] = useState("");
  const [idViewConfirmation, setIdViewConfirmation] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedDataConfirmInvoice, setSelectedDataConfirmInvoice] =
    useState<SireReviewInvoice>();
  const [totalPageNumber, setTotalPageNumber] = useState(1);
  const [dataConfirmation, setDataConfirmation] = useState<any>(null);
  const [idConfirmationPayment, setIdConfirmationPayment] = useState("");

  const handleNavAdd = () => {
    navigate("/manage-invoices/sire-inspector/add");
  };

  const handleChangeSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleSubmitSearch = () => {
    getData();
  };

  const handleEdit = (idEdit: string) => {
    navigate("/manage-invoices/sire-inspector/edit", { state: { id: idEdit } });
  };

  const handleDelete = (id: string) => {
    setId(id);
    onOpen();
  };

  const handleConfirmDelete = async () => {
    const response = await deleteSireReviewReviewerInvoice(id, name);
    if (response.status === HttpStatusCode.Ok) {
      getData();
      toast({
        title: "Invoice deleted.",
        description: "We've deleted invoice for you.",
        status: "success",
        duration: DURATION_TOAST,
        isClosable: true,
      });
    } else {
      toast({
        title: "Invoice failed to delete.",
        description: "We failed to delete invoice for you.",
        status: "error",
        duration: DURATION_TOAST,
        isClosable: true,
      });
    }

    onClose();
  };

  const handleOpenConfirmInvoice = (idView: string) => {
    //setSelectedSequenceId(sequenceID);
    setApprovalStatus("-1");
    setErrorMessageApproval("");
    setErrorMessageNote("");
    setIdViewConfirmation(idView);
    const data = dataList.find((x) => x.id === idView);
    if (data) {
      setSelectedDataConfirmInvoice(data);
    }

    onOpenConfirmInvoice();
  };

  const handleConfirmToCancelConfirmation = () => {
    setApprovalStatus("");
    onCloseConfirmPayment();
  };

  const handleCancelInvoiceConfirmation = () => {
    setApprovalStatus("");
		setApprovalNote("")
    onCloseConfirmInvoice();
  };

	const handleConfirmInvoice = async (approvalStatus: string) => {
    if (approvalStatus === "3" && approvalNote.trim() === "") {
      setErrorMessageNote("Note is required");
      return;
    }

    if (approvalStatus === "-1") {
      setErrorMessageApproval("Please choose either approve or reject.");
      return;
    }

    if (errorMessageNote) {
      return;
    }

		try {
			const obj = {
				id: idViewConfirmation,
				invoiceStatus: Number(approvalStatus),
				notes: approvalNote,
				modifiedBy: name || "System",
			};

			const response = await postUpdateInvoiceStatusInspector(obj);
			if (response.status === HttpStatusCode.Ok) {
				getData();
				toast({
					title: "Invoice status updated.",
					description: "We've updated invoice status for you.",
					status: "success",
					duration: DURATION_TOAST,
					isClosable: true,
				});
			} else {
				throw new Error(response.message ?? "Something went wrong!")
			}
		} catch {
			toast({
				title: "Invoice failed to update.",
				description: "We failed to update invoice status for you.",
				status: "error",
				duration: DURATION_TOAST,
				isClosable: true,
			});
		} finally {
			setApprovalNote("")
			onCloseConfirmInvoice();
		}




  };

  const handlePay = (idPay: string) => {
    navigate("/manage-invoices/sire-inspector/payment", {
      state: { id: idPay, type: SIRE_REVIEW_INVOICE_TYPE.INSPECTOR },
    });
  };

  const handleChangeApprovalStatus = (value: string) => {
    if (value !== "-1") {
      setErrorMessageApproval("");
    }
    setApprovalStatus(value);
  };

  const handleChangeNote = (value: string) => {
    setErrorMessageNote("");

    if (value) {
      if (value.length > 50) {
        setErrorMessageNote("Max length is 50 characters");
      }

      const result = ALLOWED_CHARACTERS_REGEX.test(value);
      if (result) {
        setErrorMessageNote("");
      } else {
        setErrorMessageNote(
          "Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.",
        );
      }
    } else {
      setErrorMessageNote("Note is required");
    }

    setApprovalNote(value);
  };

  // const handleMultiplePayment = () => {
  //     if (isValidToPay) {
  //         onOpenBulk();
  //         return;
  //     }

  //     alert(`Please make sure you're selected same company or at least one checked`);
  // }

  // const handleConfirmToCancelBulk = () => {
  //     onCloseBulk();
  // }

  // const handleDrop = (acceptedFile: File[]) => {
  //     setUploadedFile(acceptedFile);
  // };

  // const [isLoadingBulk, setIsLoadingBulk] = useState(false);

  // const handleConfirmBulk = async () => {
  //     let listPromise = [] as any;
  //     if (!bulkPaymentDate) {
  //         return;
  //     }

  //     setIsLoadingBulk(true);
  //     dataRaw.data.forEach(async (element: any) => {
  //         const data = new FormData();
  //         data.append('invoiceId', element.id);
  //         data.append('paymentDate', bulkPaymentDate ?? '');
  //         data.append('paymentStatus', '1');
  //         data.append('modifiedBy', name || "System");
  //         data.append('file', uploadedFile ? uploadedFile[0] : '');
  //         // for (let [key, value] of data.entries()) {
  //         //     console.log(`${key}: ${value}`);
  //         // }
  //         listPromise.push(updatePaymentStatusInspectorOnlyAxios(data) as any);
  //     });

  //     const responses = await Promise.all(listPromise);
  //     const results = responses.map(response => response.status);
  //     const allStatusAre200 = results.every((item: any) => item === HttpStatusCode.Ok);
  //     setIsLoadingBulk(false);
  //     if (allStatusAre200) {
  //         toast({
  //             title: `${results.length} invoice status updated.`,
  //             description: `We've updated ${results.length} invoice status for you.`,
  //             status: 'success',
  //             duration: DURATION_TOAST,
  //             isClosable: true,
  //         });

  //         setTimeout(() => {
  //             onCloseBulk();
  //         }, 1000);
  //     } else {
  //         toast({
  //             title: `Invoice status failed to update`,
  //             description: `We failed to update invoice status for you.`,
  //             status: 'error',
  //             duration: DURATION_TOAST,
  //             isClosable: true,
  //         });
  //     }
  // }

  const handleChangeInvoiceStatus = (value: number) => {
    setInvoiceStatus(String(value));
  };

  const handleChangePaymentStatus = (value: number) => {
    setPaymentStatus(String(value));
  };

  const handleDownload = async (idFile: string) => {
    await downloadFile(idFile);
  };

  const handleDownloadZip = async (idFile: string, uploadType: number) => {
    await downloadFileZip(idFile, uploadType);
  };

  const setLatestCounterAdmin = async () => {
    const responseCounter = (await getNewRecordCounterSuperAdmin()) as any;
    if (responseCounter?.status === HttpStatusCode.Ok) {
      dispatch(
        setDataTotalNewInvoiceInspectingCompany(
          responseCounter?.data?.invoiceInspectingCompanyAdmin || 0,
        ),
      );
    }
  };

  const setLatestCounterAdminFinance = async () => {
    const responseCounter = (await getNewRecordCounterAdminFinance()) as any;
    if (responseCounter?.status === HttpStatusCode.Ok) {
      dispatch(
        setDataTotalNewInvoiceInspectingCompany(
          responseCounter?.data?.inspectingCompany || 0,
        ),
      );
      dispatch(
        setDataTotalNewInvoiceShipOperator(
          responseCounter?.data?.shipOperator || 0,
        ),
      );
    }
  };

  const setLatestCounterInspectingCompany = async () => {
    const responseCounter = (await getNewRecordCounterInspectingCompany(
      inspectorId,
    )) as any;
    if (responseCounter?.status === HttpStatusCode.Ok) {
      dispatch(
        setDataTotalNewInvoiceInspectingCompany(
          responseCounter?.data?.invoices || 0,
        ),
      );
    }
  };

  const handleOpenConfirmPayment = async (idPay: string) => {
    setErrorMessageNote("");
    setApprovalNote("");
    setIdConfirmationPayment(idPay);
    const data = dataList.find((x: any) => x.id === idPay) as any;
    let newData = data;
    if (data) {
      newData = {
        ...data,
        invoiceID: data.invoiceNumber,
        month: data.reviewMonth,
        year: data.reviewYear,
        totalReviews: data.totalReview,
        downloadId: data.paymentFileId,
      };
    }
    setDataConfirmation(newData);
    onOpenConfirmPayment();
  };

  const handleSubmitConfirmPayment = async () => {
		try {
			const data = new FormData();
			data.append("id", idConfirmationPayment);
			data.append("paymentDate", dataConfirmation.paymentDate);
			data.append("paymentStatus", String(PAYMENT_STATUS.COMPLETED));
			data.append("totalAmountIDR", dataConfirmation.totalAmountIDR);
			data.append("modifiedBy", name || "System");
			// for (let [key, value] of data.entries()) {
			//     console.log(`${key}: ${value}`);
			// }
			const response = await postUpdateSireReviewReviewerInvoicePayment(data);
			if (response.status === HttpStatusCode.Ok) {
				getData();
				toast({
					title: "Payment confirmed.",
					description: "We've confirmed the payment.",
					status: "success",
					duration: DURATION_TOAST,
					isClosable: true,
				});
			} else {
				throw new Error(response.message ?? "Something went wrong!")
			}
		} catch {
			toast({
				title: "Invoice failed to update.",
				description: "We failed to confirm the payment.",
				status: "error",
				duration: DURATION_TOAST,
				isClosable: true,
			});
		} finally {
			onCloseConfirmPayment();
		}
  };

  const getData = async () => {
    try {
      const handleSuccessfulResponse = (response: ListSireReviewInvoices) => {
        setDataList(response.data);
        setTotalPageNumber(response.totalPage);
        setCurrentPageNumber(response.pageNumber);
        setHasNextPage(response.hasNextPage);
      };

      const params = {
        search: searchValue,
        pageNumber: currentPageNumber === 0 ? 1 : currentPageNumber,
        pageSize: currentPageSize,
        status: "",
        invoiceStatus: invoiceStatus,
        paymentStatus: paymentStatus,
      };

      let response;

      if (role === ROLES.SUPER_ADMIN) {
        response = await getListSireReviewInvoices(params);
        setLatestCounterAdmin();
      } else if (role === ROLES.FINANCE) {
        response = await getListSireReviewInvoicesForFinance(params);
        setLatestCounterAdminFinance();
      } else if (role === ROLES.INSPECTING_COMPANY) {
        const newParam = { ...params, reviewerId: inspectorId };
        response = await getListSireReviewInvoicesByReviewer(newParam);
        setLatestCounterInspectingCompany();
      } else {
        throw new Error("Invalid user role");
      }

      if (response.status === 200) {
        handleSuccessfulResponse(response);
      } else {
        throw new Error(response.message ?? "Failed to fetching data");
      }
    } catch (error) {
      toast({
        description: (error as Error).message,
        status: "error",
        duration: DURATION_TOAST,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalSireReviewerPaymentConfirm
                isOpen={isOpenConfirmPayment}
                onClose={onCloseConfirmPayment}
                handleCancel={handleConfirmToCancelConfirmation}
                handleConfirm={handleSubmitConfirmPayment}
                dataConfirmation={dataConfirmation}
                handleDownload={handleDownload}
            />
            {/* <ModalBulkPaymentConfirmation isOpen={isOpenBulk} onClose={onCloseBulk} isLoadingBulk={isLoadingBulk} handleConfirmToCancel={handleConfirmToCancelBulk} handleConfirm={handleConfirmBulk} bulkPaymentDate={bulkPaymentDate} setBulkPaymentDate={setBulkPaymentDate} handleDrop={handleDrop} uploadedFile={uploadedFile} /> */}
            {/*<ModalInvoiceConfirmation isOpen={isOpenConfirmInvoice} onClose={onCloseConfirmInvoice} handleChangeNote={handleChangeNote} handleConfirmToCancel={handleConfirmToCancel} handleConfirm={handleConfirmView} approvalStatus={approvalStatus} handleChangeApprovalStatus={handleChangeApprovalStatus} selectedDataView={selectedDataView} errorMessageNote={errorMessageNote} />*/}
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <ModalSireReviewerInvoiceConfirm
                isOpen={isOpenConfirmInvoice}
                onClose={onCloseConfirmInvoice}
                handleCancel={handleCancelInvoiceConfirmation}
                handleConfirm={handleConfirmInvoice}
                dataConfirmation={selectedDataConfirmInvoice}
                approvalStatus={approvalStatus}
                handleChangeApprovalStatus={handleChangeApprovalStatus}
                handleChangeNote={handleChangeNote}
                errorMessageApproval={errorMessageApproval}
                errorMessageNote={errorMessageNote}
            />
            <div className="table-container">
                {role === 'inspecting company' &&
                    <div className="table-filter-action-container">
                        <div className="table-filter-container">
                          <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <div className="filter-input-width-8">
                                  <Input placeholder='Search' className="table-filter-input" onChange={(e: any) => handleChangeSearch(e.target.value)} />
                              </div>
                              <div className="filter-input-width-6">
                                  <Select className="table-filter-input" onChange={(e: any) => handleChangePaymentStatus(e.target.value)}>
                                      <option key="all" value=''>All Payment Status</option>
                                      <option key="unpaid" value={0}>Unpaid</option>
                                      <option key="paid" value={1}>Paid</option>
                                  </Select>
                              </div>
                              <div>
                                  <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                      Search
                                  </Button>
                              </div>
                              <div className="table-filter-separator"/>
                                <Button leftIcon={<AddIcon />} className='table-add-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleNavAdd}>
                                  Create Invoice
                                </Button>
                              </div>
                            </div>
                        </div>
                    </div>
                }
                {(role === 'super admin' || role === 'admin finance') &&
                    <div className="table-filter-action-container">
                        <div className="table-filter-container">
                            <div className="table-filter-input filter-input-width-8">
                                <Input placeholder='Search' onChange={(e: any) => handleChangeSearch(e.target.value)} />
                            </div>
                            <div className="table-filter-input filter-input-width-6">
                                <Select onChange={(e: any) => handleChangeInvoiceStatus(e.target.value)}>
                                    <option key="all" value=''>All Invoice Status</option>
                                    <option key="approved" value={String(INVOICE_STATUS.APPROVED)}>Approved</option>
                                    <option key="new" value={String(INVOICE_STATUS.IN_PROGRESS)}>New</option>
                                    <option key="reject" value={String(INVOICE_STATUS.REJECTED)}>Rejected</option>
                                </Select>
                            </div>
                            <div className="table-filter-input filter-input-width-6">
                                <Select onChange={(e: any) => handleChangePaymentStatus(e.target.value)}>
                                    <option key="all" value=''>All Payment Status</option>
                                    <option key="unpaid" value={String(PAYMENT_STATUS.UNPAID)}>Unpaid</option>
                                    <option key="paid" value={String(PAYMENT_STATUS.PAID)}>Paid</option>
                                    <option key="completed" value={String(PAYMENT_STATUS.COMPLETED)}>Completed</option>
                                </Select>
                            </div>
                            <div>
                                <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                    Search
                                </Button>
                            </div>
                        </div>
                    </div>
                }
                <TableInvoiceSireReviewInspector
                    dataList={dataList}
                    isLoading={isLoading}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                    //handleView={handleView}
                    handlePay={handlePay}
                    //handleDownload={handleDownload}
                    handleDownloadZip={handleDownloadZip}
                    handleConfirmInvoice={handleOpenConfirmInvoice}
                    handleConfirmPayment={handleOpenConfirmPayment}
                    role={role}
                />
            </div>
        </>
    )
}

export default InvoicesSireReviewInspectorList;
